import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useApi } from '../hooks/useApi';
import { canAccessPyl } from '../utils/pylAccess';
import { canAccessPresupuestos } from '../utils/empresarialAccess';
import { canAccessCobranzasEssential, canAccessProfesionalFeatures } from '../utils/profesionalAccess';
import { FiTrendingUp } from 'react-icons/fi';
import { FormAlert } from '../components/form';
import PylCurrencyBreakdown from '../components/PylCurrencyBreakdown';
import ProjectedCashflowPanel from '../components/ProjectedCashflowPanel';
import BudgetPanel from '../components/BudgetPanel';
import ConsolidatedReportPanel from '../components/ConsolidatedReportPanel';
import CollectionReportPanel from '../components/CollectionReportPanel';
import PlanUpgradeCard from '../components/PlanUpgradeCard';
import CurrencyViewToggle from '../components/CurrencyViewToggle';
import { formatMoney as formatMoneyCur } from '../utils/money';
import { runRateCopy } from '../utils/fiscal';
import { downloadBinaryExport } from '../utils/downloadExport';
import CurrencyBadge from '../components/CurrencyBadge';
import {
  VISTA_CONSOLIDADO,
  pickFlujo,
  pickPyl,
  pickPylComparacion,
  vistaHint,
  shouldShowCurrencyToggle,
  displayCurrency,
} from '../utils/currencyView';

const PERIODOS = [
  { id: 'mes', label: 'Este mes', short: 'Mes' },
  { id: 'mes-pasado', label: 'Mes pasado', short: 'Anterior' },
  { id: 'trimestre', label: 'Este trimestre', short: 'Trim.' },
  { id: 'año', label: 'Este año', short: 'Año' },
  { id: 'todo', label: 'Todo el historial', short: 'Todo' },
];

const TABS = [
  { id: 'flujo', label: 'Flujo de caja', short: 'Flujo' },
  { id: 'cobranzas', label: 'Análisis de cobranza', short: 'Cobranza' },
  { id: 'pyl', label: 'Estado de resultados', short: 'Resultados' },
  { id: 'presupuestos', label: 'Presupuestos', short: 'Presup.' },
];

function getAñosDisponibles() {
  const añoActual = new Date().getFullYear();
  const años = [];
  for (let a = 2020; a <= añoActual + 1; a++) {
    años.push(a);
  }
  return años.reverse();
}

function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseLocalDate(isoDate) {
  if (!isoDate) return null;
  const [y, m, d] = String(isoDate).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function getRango(periodoId) {
  const hoy = new Date();
  let desde, hasta;
  if (periodoId === 'mes') {
    desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
  } else if (periodoId === 'mes-pasado') {
    desde = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    hasta = new Date(hoy.getFullYear(), hoy.getMonth(), 0);
  } else if (periodoId === 'trimestre') {
    const t = Math.floor(hoy.getMonth() / 3) + 1;
    desde = new Date(hoy.getFullYear(), (t - 1) * 3, 1);
    hasta = new Date(hoy.getFullYear(), t * 3, 0);
  } else if (periodoId === 'año') {
    desde = new Date(hoy.getFullYear(), 0, 1);
    hasta = new Date(hoy.getFullYear(), 11, 31);
  } else if (periodoId === 'todo') {
    desde = new Date(2000, 0, 1);
    hasta = new Date(2030, 11, 31);
  } else {
    desde = new Date(hoy.getFullYear(), 0, 1);
    hasta = new Date(hoy.getFullYear(), 11, 31);
  }
  return {
    desde: formatLocalDate(desde),
    hasta: formatLocalDate(hasta)
  };
}

function PylFxChips({ porMoneda, field, modoOscuro, textMuted }) {
  if (!porMoneda) return null;
  const entries = Object.entries(porMoneda).filter(([, s]) => Number(s?.[field]) !== 0);
  if (entries.length < 2 && !entries.some(([m]) => m !== 'ARS')) return null;

  return (
    <div className="flex flex-wrap gap-1.5 pb-2 pl-3 sm:pl-6">
      {entries.map(([moneda, s]) => {
        const native = Number(s[field]) || 0;
        const arsHint = moneda !== 'ARS' && field === 'ingresos' && s.ingresosArs
          ? s.ingresosArs
          : moneda !== 'ARS' && field === 'resultadoOperativo' && s.resultadoArs
            ? s.resultadoArs
            : null;
        return (
          <span
            key={`${moneda}-${field}`}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] sm:text-[11px] tabular-nums ${
              modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600'
            }`}
          >
            <CurrencyBadge currency={moneda} modoOscuro={modoOscuro} size="xs" />
            {formatMoneyCur(native, moneda, { showCode: moneda !== 'ARS' })}
            {arsHint != null && (
              <span className={textMuted}>≈ {formatMoneyCur(arsHint, 'ARS')}</span>
            )}
          </span>
        );
      })}
    </div>
  );
}

function PylLine({ label, amount, formatMoney, indent = false, bold = false, muted = false, modoOscuro, textPri, textMuted, children }) {
  const color = muted
    ? textMuted
    : amount < 0
      ? (modoOscuro ? 'text-red-300' : 'text-red-700')
      : textPri;
  return (
    <div className={`${indent ? 'pl-3 sm:pl-6' : ''}`}>
      <div className={`flex items-start justify-between gap-3 py-1.5 ${bold ? 'font-semibold' : ''}`}>
        <span className={`text-xs sm:text-sm min-w-0 ${bold ? textPri : muted ? textMuted : textPri}`}>{label}</span>
        <span className={`text-xs sm:text-sm tabular-nums text-right shrink-0 ${bold ? 'sm:text-base' : ''} ${color}`}>
          {formatMoney(amount)}
        </span>
      </div>
      {children}
    </div>
  );
}

function PylStatement({ pyl, vista, onVistaChange, rango, modoOscuro, box, textPri, textSec, textMuted, divide, formatMoney, onExport, exporting }) {
  const view = pickPyl(pyl, vista);
  const fmt = (n) => formatMoneyCur(n ?? 0, view.currency);
  const comparacion = pickPylComparacion(pyl.comparacionPeriodo, vista);
  const showFx = shouldShowCurrencyToggle(pyl.monedas || Object.keys(pyl.porMoneda || pyl.desgloseMoneda || {}));
  const showChips = vista === VISTA_CONSOLIDADO && showFx;
  const resultadoMonto = view.resultadoOperativo;
  const resultColor = resultadoMonto >= 0
    ? (modoOscuro ? 'text-green-300' : 'text-green-700')
    : (modoOscuro ? 'text-red-300' : 'text-red-700');

  return (
    <div className="space-y-2 sm:space-y-3">
      <div className={`rounded-2xl border ${box} p-3 sm:p-5`}>
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className={`text-sm sm:text-base font-semibold ${textPri}`}>Estado de resultados</h2>
              <CurrencyBadge currency={view.currency} modoOscuro={modoOscuro} size="xs" />
              {view.consolidado && showFx && (
                <span className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>en pesos</span>
              )}
            </div>
            <p className={`text-[11px] sm:text-xs mt-0.5 ${textMuted}`}>
              Base devengada · {rango.desde} – {rango.hasta}. Ingresos facturados, cobrados o no.
              {vista === VISTA_CONSOLIDADO && showFx
                ? ' Totales convertidos a pesos con el TC de cada registro.'
                : vista !== VISTA_CONSOLIDADO
                  ? ` Solo movimientos en ${vista}.`
                  : ''}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {vista !== VISTA_CONSOLIDADO && showFx && (
              <button
                type="button"
                onClick={() => onVistaChange?.(VISTA_CONSOLIDADO)}
                className={`px-2.5 py-1.5 rounded-md border text-xs font-semibold ${
                  modoOscuro ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                Ver en pesos
              </button>
            )}
            {onExport && (
              <button
                type="button"
                onClick={onExport}
                disabled={exporting}
                className={`inline-flex items-center px-3 py-1.5 rounded-md border text-xs font-semibold disabled:opacity-50 ${
                  modoOscuro ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                {exporting ? 'Exportando…' : 'CSV'}
              </button>
            )}
          </div>
        </div>

        {showFx && pyl.desgloseMoneda && (
          <PylCurrencyBreakdown
            desgloseMoneda={pyl.desgloseMoneda}
            equivalenteArs={pyl.equivalenteArs}
            vista={vista}
            modoOscuro={modoOscuro}
            textPri={textPri}
            textMuted={textMuted}
            onSelectVista={onVistaChange}
          />
        )}

        {pyl.sinCotizacion > 0 && (
          <p className={`text-xs mb-3 rounded-lg px-3 py-2 ${modoOscuro ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-800'}`}>
            {pyl.sinCotizacion} registro(s) en moneda extranjera sin tipo de cambio. Completá la cotización para incluirlos en el consolidado ARS.
          </p>
        )}

        <div className={`divide-y ${divide}`}>
          <PylLine label={pyl.ingresos?.netoDeIva ? '(+) Ingresos por ventas (neto de IVA)' : '(+) Ingresos por ventas'} amount={view.ingresos} formatMoney={fmt} bold modoOscuro={modoOscuro} textPri={textPri} textMuted={textMuted}>
            {showChips && <PylFxChips porMoneda={pyl.porMoneda} field="ingresos" modoOscuro={modoOscuro} textMuted={textMuted} />}
            {view.porCategoria?.length > 0 && view.porCategoria.some((c) => c.categoria && c.categoria !== 'Sin categoría') && (
              <div className={`pb-2 ${textMuted}`}>
                {view.porCategoria.map((row) => (
                  <div key={row.categoria} className="flex justify-between gap-3 text-[11px] sm:text-xs py-0.5 pl-3 sm:pl-6">
                    <span className="min-w-0 truncate">{row.categoria}</span>
                    <span className="tabular-nums shrink-0">{fmt(row.monto)}</span>
                  </div>
                ))}
              </div>
            )}
            <p className={`text-xs pb-2 ${textMuted}`}>{view.cantidad || pyl.ingresos?.cantidad || 0} comprobantes</p>
          </PylLine>

          <PylLine label="(−) Costo de ventas (Insumos)" amount={-(view.costoVentas || 0)} formatMoney={fmt} modoOscuro={modoOscuro} textPri={textPri} textMuted={textMuted}>
            {showChips && <PylFxChips porMoneda={pyl.porMoneda} field="costoVentas" modoOscuro={modoOscuro} textMuted={textMuted} />}
            {view.costoDetalle?.map((row) => (
              <div key={row.categoria} className={`flex justify-between gap-3 text-[11px] sm:text-xs py-0.5 pl-3 sm:pl-6 ${textMuted}`}>
                <span className="min-w-0 truncate">{row.categoria}</span>
                <span className="tabular-nums shrink-0">{fmt(row.monto)}</span>
              </div>
            ))}
          </PylLine>

          <PylLine label="Utilidad bruta" amount={view.utilidadBruta} formatMoney={fmt} bold modoOscuro={modoOscuro} textPri={textPri} textMuted={textMuted}>
            {showChips && <PylFxChips porMoneda={pyl.porMoneda} field="utilidadBruta" modoOscuro={modoOscuro} textMuted={textMuted} />}
          </PylLine>

          <PylLine label="(−) Gastos operativos" amount={-(view.gastosOperativos || 0)} formatMoney={fmt} modoOscuro={modoOscuro} textPri={textPri} textMuted={textMuted}>
            {showChips && <PylFxChips porMoneda={pyl.porMoneda} field="gastosOperativos" modoOscuro={modoOscuro} textMuted={textMuted} />}
            {view.opexDetalle?.map((row) => (
              <div key={row.categoria} className={`flex justify-between gap-3 text-[11px] sm:text-xs py-0.5 pl-3 sm:pl-6 ${textMuted}`}>
                <span className="min-w-0 truncate">{row.categoria}</span>
                <span className="tabular-nums shrink-0">{fmt(row.monto)}</span>
              </div>
            ))}
          </PylLine>

          <PylLine label="(−) Impuestos registrados" amount={-(view.impuestos || 0)} formatMoney={fmt} modoOscuro={modoOscuro} textPri={textPri} textMuted={textMuted}>
            {showChips && <PylFxChips porMoneda={pyl.porMoneda} field="impuestos" modoOscuro={modoOscuro} textMuted={textMuted} />}
            {view.impuestosDetalle?.map((row) => (
              <div key={row.categoria} className={`flex justify-between gap-3 text-[11px] sm:text-xs py-0.5 pl-3 sm:pl-6 ${textMuted}`}>
                <span className="min-w-0 truncate">{row.categoria}</span>
                <span className="tabular-nums shrink-0">{fmt(row.monto)}</span>
              </div>
            ))}
          </PylLine>

          <div className={`${modoOscuro ? 'bg-gray-800/50' : 'bg-gray-50'} -mx-3 sm:-mx-5 px-3 sm:px-5 rounded-b-xl`}>
            <div className="flex items-start justify-between gap-3 py-2.5">
              <span className={`text-xs sm:text-sm font-semibold ${textPri}`}>Resultado operativo</span>
              <span className={`text-sm sm:text-lg font-semibold tabular-nums text-right shrink-0 ${resultColor}`}>
                {fmt(resultadoMonto)}
              </span>
            </div>
            {showChips && <PylFxChips porMoneda={pyl.porMoneda} field="resultadoOperativo" modoOscuro={modoOscuro} textMuted={textMuted} />}
          </div>
        </div>

        {pyl.sinCategoria > 0 && vista === VISTA_CONSOLIDADO && (
          <p className={`text-xs mt-3 ${textMuted}`}>
            Hay {formatMoney(pyl.sinCategoria)} en egresos sin categoría: se contabilizan como gastos operativos. Clasificalos para un estado de resultados más preciso.
          </p>
        )}
      </div>

      <div className={`rounded-2xl border ${box} p-3 sm:p-5`}>
        <h3 className={`text-sm font-semibold mb-1 ${textPri}`}>Facturado vs cobrado</h3>
        <p className={`text-[11px] sm:text-xs mb-2 ${textSec}`}>
          El estado de resultados usa facturación. El flujo usa solo lo cobrado menos egresos.
          {vista !== VISTA_CONSOLIDADO ? ` Cifras en ${vista}.` : showFx ? ' Cifras en pesos.' : ''}
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div className={`p-2.5 rounded-xl ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>Cobrado</p>
            <p className={`text-sm font-semibold tabular-nums break-words ${modoOscuro ? 'text-green-300' : 'text-green-700'}`}>
              {fmt(view.ventasCobradas)}
            </p>
          </div>
          <div className={`p-2.5 rounded-xl ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>Pendiente</p>
            <p className={`text-sm font-semibold tabular-nums break-words ${modoOscuro ? 'text-amber-300' : 'text-amber-700'}`}>
              {fmt(view.ventasImpagas)}
            </p>
          </div>
          <div className={`p-2.5 rounded-xl col-span-2 sm:col-span-1 ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>Dif. devengado</p>
            <p className={`text-sm font-semibold tabular-nums break-words ${textPri}`}>
              {fmt(view.diferenciaDevengadoCobrado)}
            </p>
          </div>
        </div>
      </div>

      {comparacion && (
        <div className={`rounded-2xl border ${box} p-3 sm:p-5`}>
          <h3 className={`text-sm font-semibold mb-1 ${textPri}`}>Vs período anterior</h3>
          <p className={`text-[11px] sm:text-xs mb-2 ${textSec}`}>
            {comparacion.desde} – {comparacion.hasta}
            {vista !== VISTA_CONSOLIDADO ? ` · ${vista}` : ''}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div className={`p-2.5 rounded-xl ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>Ingresos ant.</p>
              <p className={`text-sm font-semibold tabular-nums break-words ${textPri}`}>{fmt(comparacion.ingresos)}</p>
              <p className={`text-[11px] mt-0.5 ${comparacion.variacionIngresos >= 0 ? (modoOscuro ? 'text-green-300' : 'text-green-700') : (modoOscuro ? 'text-red-300' : 'text-red-700')}`}>
                {comparacion.variacionIngresos >= 0 ? '+' : ''}{fmt(comparacion.variacionIngresos)}
              </p>
            </div>
            <div className={`p-2.5 rounded-xl ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>Resultado ant.</p>
              <p className={`text-sm font-semibold tabular-nums break-words ${textPri}`}>{fmt(comparacion.resultadoOperativo)}</p>
              <p className={`text-[11px] mt-0.5 ${comparacion.variacionResultado >= 0 ? (modoOscuro ? 'text-green-300' : 'text-green-700') : (modoOscuro ? 'text-red-300' : 'text-red-700')}`}>
                {comparacion.variacionResultado >= 0 ? '+' : ''}{fmt(comparacion.variacionResultado)}
              </p>
            </div>
          </div>
        </div>
      )}

      <FiscalPanel pyl={pyl} modoOscuro={modoOscuro} box={box} textPri={textPri} textSec={textSec} textMuted={textMuted} formatMoney={formatMoney} />
    </div>
  );
}

function FiscalPanel({ pyl, modoOscuro, box, textPri, textSec, textMuted, formatMoney }) {
  const fiscal = pyl?.fiscal;
  if (!fiscal) return null;

  const pct = Math.min(Math.max(fiscal.porcentajeTope || 0, 0), 100);
  const barColor = (fiscal.porcentajeTope || 0) >= 100
    ? 'bg-red-500'
    : (fiscal.porcentajeTope || 0) >= 80
      ? 'bg-amber-500'
      : 'bg-[#1F80FF]';
  const alertStyles = {
    danger: modoOscuro ? 'bg-red-900/30 text-red-200' : 'bg-red-50 text-red-800',
    warning: modoOscuro ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-800',
    info: modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-700'
  };

  return (
    <div className={`rounded-2xl border ${box} p-3 sm:p-5`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <h3 className={`text-sm font-semibold ${textPri}`}>Orientación fiscal</h3>
          <p className={`text-[11px] sm:text-xs mt-0.5 ${textMuted}`}>
            {fiscal.regimen === 'monotributo'
              ? `Monotributo${fiscal.categoria ? ` cat. ${fiscal.categoria}` : ''} · 12 meses`
              : fiscal.regimen === 'ri'
                ? 'Responsable inscripto'
                : 'Sin régimen declarado'}
            {Object.keys(fiscal.porMoneda || {}).some((m) => m !== 'ARS')
              ? ' · Dólares y euros convertidos a pesos con el TC de cada venta.'
              : ''}
          </p>
        </div>
        <a href="/cuenta?tab=fiscal" className="text-[11px] sm:text-xs font-semibold shrink-0 text-[#1F80FF] hover:underline">
          Editar
        </a>
      </div>

      {fiscal.regimen === 'ri' && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            <div className={`p-2.5 rounded-xl ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>IVA débito</p>
              <p className={`text-sm font-semibold tabular-nums break-words ${textPri}`}>{formatMoney(fiscal.ivaDebito ?? pyl.iva?.debito)}</p>
              <p className={`text-[11px] ${textMuted}`}>Ventas</p>
            </div>
            <div className={`p-2.5 rounded-xl ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>IVA crédito</p>
              <p className={`text-sm font-semibold tabular-nums break-words ${textPri}`}>{formatMoney(fiscal.ivaCredito ?? pyl.iva?.credito)}</p>
              <p className={`text-[11px] ${textMuted}`}>Egresos</p>
            </div>
            <div className={`p-2.5 rounded-xl col-span-2 sm:col-span-1 ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>
                {(fiscal.ivaPosicion ?? pyl.iva?.posicion ?? 0) >= 0 ? 'IVA a pagar' : 'IVA a favor'}
              </p>
              <p className={`text-sm font-semibold tabular-nums break-words ${textPri}`}>
                {formatMoney(Math.abs(fiscal.ivaPosicion ?? pyl.iva?.posicion ?? 0))}
              </p>
            </div>
          </div>
          {pyl.ingresos?.netoDeIva && (
            <p className={`text-xs mb-3 ${textMuted}`}>
              El resultado operativo usa importes netos de IVA. Lo cobrado en caja incluye IVA.
            </p>
          )}
        </>
      )}
      {fiscal.regimen === 'monotributo' && fiscal.topeCategoria != null && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            <div className={`p-2.5 rounded-xl ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>Facturado 12m</p>
              <p className={`text-sm font-semibold tabular-nums break-words ${textPri}`}>{formatMoney(fiscal.facturacionAcumulada12m)}</p>
              <p className={`text-[11px] ${textMuted}`}>en pesos</p>
            </div>
            <div className={`p-2.5 rounded-xl ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>Tope cat. {fiscal.categoria || '—'}</p>
              <p className={`text-sm font-semibold tabular-nums break-words ${textPri}`}>{formatMoney(fiscal.topeCategoria)}</p>
            </div>
            <div className={`p-2.5 rounded-xl col-span-2 sm:col-span-1 ${modoOscuro ? 'bg-gray-800' : 'bg-gray-50'}`}>
              <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>Cuota mensual</p>
              <p className={`text-sm font-semibold tabular-nums break-words ${textPri}`}>{formatMoney(fiscal.cuotaMensualEstimada)}</p>
            </div>
          </div>
          <div className={`h-2 rounded-full overflow-hidden mb-2 ${modoOscuro ? 'bg-gray-800' : 'bg-gray-200'}`}>
            <div className={`h-full ${barColor}`} style={{ width: `${pct}%` }} />
          </div>
          <p className={`text-xs mb-2 ${textMuted}`}>
            {(fiscal.porcentajeTope || 0) > 0 && (fiscal.porcentajeTope || 0) < 1
              ? '<1% del tope anual'
              : `${Math.round(fiscal.porcentajeTope || 0)}% del tope anual`}
            {runRateCopy(fiscal) ? ` · ${runRateCopy(fiscal)}` : ''}
          </p>
          {Object.entries(fiscal.porMoneda || {}).filter(([, r]) => r.cantidad > 0).length > 1 ||
          Object.keys(fiscal.porMoneda || {}).some((m) => m !== 'ARS') ? (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {Object.entries(fiscal.porMoneda || {}).filter(([, r]) => r.cantidad > 0).map(([moneda, row]) => (
                <span
                  key={moneda}
                  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] tabular-nums ${
                    modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600'
                  }`}
                >
                  <CurrencyBadge currency={moneda} modoOscuro={modoOscuro} size="xs" />
                  {formatMoneyCur(row.nativo, moneda, { showCode: moneda !== 'ARS' })}
                  {moneda !== 'ARS' && row.ars > 0 && (
                    <span>≈ {formatMoneyCur(row.ars, 'ARS')}</span>
                  )}
                </span>
              ))}
            </div>
          ) : null}
          {fiscal.sinCotizacion > 0 && (
            <p className={`text-[11px] mb-3 rounded-lg px-2 py-1.5 ${modoOscuro ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-800'}`}>
              {fiscal.sinCotizacion} venta(s) en moneda extranjera sin cotización: no entran al tope en pesos.
            </p>
          )}
        </>
      )}

      <div className="space-y-2">
        {(fiscal.alertas || []).map((a, i) => (
          <p key={i} className={`text-xs rounded-lg px-3 py-2 ${alertStyles[a.tipo] || alertStyles.info}`}>
            {a.mensaje}
          </p>
        ))}
      </div>
      <p className={`text-[11px] mt-3 ${textMuted}`}>
        Estimación orientativa con escalas ARCA vigentes {fiscal.parametria?.vigenciaDesde} a {fiscal.parametria?.vigenciaHasta}. No reemplaza liquidación oficial.
      </p>
    </div>
  );
}

function DiarioList({ dias, formatMoney, modoOscuro, textMuted, textPri }) {
  if (!dias.length) {
    return <p className={`py-3 text-center text-xs ${textMuted}`}>No hay movimientos diarios</p>;
  }
  return (
    <ul className="space-y-2">
      {dias.map((dia) => {
        const flujo = (Number(dia.ingresos) || 0) - (Number(dia.egresos) || 0);
        return (
          <li key={dia.fecha}>
            <div className="flex items-baseline justify-between gap-2">
              <span className={`text-xs font-medium ${textPri || ''}`}>
                {parseLocalDate(dia.fecha)?.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' })}
              </span>
              <span className={`text-xs tabular-nums font-semibold ${
                flujo >= 0
                  ? modoOscuro ? 'text-green-300' : 'text-green-700'
                  : modoOscuro ? 'text-red-300' : 'text-red-700'
              }`}>
                {formatMoney(flujo)}
              </span>
            </div>
            <div className={`flex justify-between gap-2 text-[11px] ${textMuted}`}>
              <span>Ing. {formatMoney(dia.ingresos)}</span>
              <span>Egr. {formatMoney(dia.egresos)}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const Reportes = () => {
  const { token, user } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const { secureFetch } = useApi();
  const location = useLocation();
  const tabParam = new URLSearchParams(location.search).get('tab');
  const initialTab = ['pyl', 'presupuestos', 'cobranzas'].includes(tabParam) ? tabParam : 'flujo';

  const [tab, setTab] = useState(initialTab);
  const [periodo, setPeriodo] = useState('mes');
  const [datos, setDatos] = useState(null);
  const [mensual, setMensual] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categorias, setCategorias] = useState([]);
  const [añoMensual, setAñoMensual] = useState(new Date().getFullYear());
  const [pyl, setPyl] = useState(null);
  const [loadingPyl, setLoadingPyl] = useState(false);
  const [exportingPyl, setExportingPyl] = useState(false);
  const [exportingPack, setExportingPack] = useState(false);
  const [currencyVista, setCurrencyVista] = useState(VISTA_CONSOLIDADO);

  const hasPylAccess = useMemo(() => canAccessPyl(user), [user]);
  const hasPresupuestosAccess = useMemo(() => canAccessPresupuestos(user), [user]);
  const hasProfesional = useMemo(() => canAccessProfesionalFeatures(user), [user]);
  const hasCobranzasEssential = useMemo(() => canAccessCobranzasEssential(user), [user]);
  const [proyeccion, setProyeccion] = useState(null);
  const [loadingProyeccion, setLoadingProyeccion] = useState(false);
  
  // Estado para desglose diario
  const [mesExpandido, setMesExpandido] = useState(null);
  const [datosDiarios, setDatosDiarios] = useState({});
  const [cargandoDiario, setCargandoDiario] = useState(false);

  const rango = useMemo(() => getRango(periodo), [periodo]);

  const fetchFlujoCaja = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const { desde, hasta } = rango;
      const [resFlujo, resCat] = await Promise.all([
        secureFetch(`/api/reportes/flujo-caja?desde=${desde}&hasta=${hasta}`),
        secureFetch(`/api/reportes/categorias?desde=${desde}&hasta=${hasta}`)
      ]);
      if (!resFlujo.ok) throw new Error('Error al cargar el reporte');
      const data = await resFlujo.json();
      setDatos(data);
      if (resCat.ok) {
        const catData = await resCat.json();
        setCategorias(catData.categorias || []);
      } else {
        setCategorias([]);
      }
    } catch (err) {
      setError(err.message || 'Error de conexión');
      setDatos(null);
      setCategorias([]);
    } finally {
      setLoading(false);
    }
  }, [token, secureFetch, rango]);

  const fetchMensual = useCallback(async () => {
    if (!token) return;
    try {
      const res = await secureFetch(`/api/reportes/flujo-caja-mensual?anno=${añoMensual}`);
      if (!res.ok) return;
      const data = await res.json();
      setMensual(data);
    } catch {
      setMensual(null);
    }
  }, [token, secureFetch, añoMensual]);

  const fetchDiario = useCallback(async (anno, mes) => {
    const key = `${anno}-${mes}`;
    if (datosDiarios[key]) return;
    setCargandoDiario(true);
    try {
      const res = await secureFetch(`/api/reportes/flujo-caja-diario?anno=${anno}&mes=${mes}`);
      if (res.ok) {
        const data = await res.json();
        setDatosDiarios(prev => ({ ...prev, [key]: data.dias }));
      }
    } catch {
      // Silenciar error
    } finally {
      setCargandoDiario(false);
    }
  }, [secureFetch, datosDiarios]);

  const toggleMes = (anno, mes) => {
    const key = `${anno}-${mes}`;
    if (mesExpandido === key) {
      setMesExpandido(null);
    } else {
      setMesExpandido(key);
      fetchDiario(anno, mes);
    }
  };

  const fetchPyl = useCallback(async () => {
    if (!token || !hasPylAccess) return;
    setLoadingPyl(true);
    setError('');
    try {
      const { desde, hasta } = rango;
      const res = await secureFetch(`/api/reportes/pyl?desde=${desde}&hasta=${hasta}`);
      if (res.status === 403) {
        setPyl(null);
        throw new Error('El estado de resultados está disponible en el plan Profesional o superior.');
      }
      if (!res.ok) throw new Error('Error al cargar el estado de resultados');
      setPyl(await res.json());
    } catch (err) {
      setError(err.message || 'Error de conexión');
      setPyl(null);
    } finally {
      setLoadingPyl(false);
    }
  }, [token, secureFetch, rango, hasPylAccess]);

  const exportarPylCsv = useCallback(async () => {
    if (!token || !hasPylAccess) return;
    setExportingPyl(true);
    setError('');
    try {
      const { desde, hasta } = rango;
      const res = await secureFetch(`/api/export/pyl?desde=${desde}&hasta=${hasta}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Error al exportar el estado de resultados');
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `estado-resultados-${desde}_${hasta}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Error al exportar');
    } finally {
      setExportingPyl(false);
    }
  }, [token, hasPylAccess, rango, secureFetch]);

  const descargarPackContador = useCallback(async () => {
    if (!token || !hasPylAccess) return;
    setExportingPack(true);
    setError('');
    try {
      const { desde, hasta } = rango;
      await downloadBinaryExport(
        secureFetch,
        `/api/export/contador-pack?desde=${desde}&hasta=${hasta}`,
        `contsuite-contador-${String(desde).slice(0, 7)}.zip`
      );
    } catch (err) {
      setError(err.message || 'Error al descargar el pack');
    } finally {
      setExportingPack(false);
    }
  }, [token, hasPylAccess, rango, secureFetch]);

  const fetchProyeccion = useCallback(async () => {
    if (!token || !hasProfesional) {
      setProyeccion(null);
      return;
    }
    setLoadingProyeccion(true);
    try {
      const vista = currencyVista !== VISTA_CONSOLIDADO ? `&vista=${encodeURIComponent(currencyVista)}` : '';
      const res = await secureFetch(`/api/reportes/flujo-caja-proyectado?meses=3${vista}`);
      if (res.ok) {
        setProyeccion(await res.json());
      } else {
        setProyeccion(null);
      }
    } catch {
      setProyeccion(null);
    } finally {
      setLoadingProyeccion(false);
    }
  }, [token, hasProfesional, secureFetch, currencyVista]);

  useEffect(() => {
    fetchFlujoCaja();
  }, [fetchFlujoCaja]);

  useEffect(() => {
    fetchMensual();
  }, [fetchMensual]);

  useEffect(() => {
    if (tab === 'flujo' && hasProfesional) fetchProyeccion();
  }, [tab, hasProfesional, fetchProyeccion]);

  useEffect(() => {
    if (tab === 'pyl' && hasPylAccess) fetchPyl();
  }, [tab, hasPylAccess, fetchPyl]);

  useEffect(() => {
    const next = new URLSearchParams(location.search).get('tab');
    if (['flujo', 'pyl', 'presupuestos', 'cobranzas'].includes(next)) setTab(next);
  }, [location.search]);


  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-700';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const btnSec = modoOscuro ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-900 hover:bg-gray-200';
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const divideY = modoOscuro ? 'divide-gray-700' : 'divide-gray-200';
  const tabInactive = modoOscuro ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50';
  const pillIdle = modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600 border border-gray-200';

  const formatMoney = (n) => formatMoneyCur(n ?? 0, 'ARS');
  const flujoVista = useMemo(() => pickFlujo(datos, currencyVista), [datos, currencyVista]);
  const flujoCurrency = displayCurrency(currencyVista);
  const formatFlujo = (n) => formatMoneyCur(n ?? 0, flujoCurrency);
  const showCurrencyToggle = shouldShowCurrencyToggle(
    tab === 'pyl' ? (pyl?.monedas || Object.keys(pyl?.porMoneda || {})) : (datos?.monedas || [])
  );

  return (
    <div className={`${modoOscuro ? 'w-full min-h-[calc(100dvh-4rem)] bg-black text-white' : 'w-full min-h-[calc(100dvh-4rem)] bg-[#f5f5f7] text-gray-900'} overflow-x-hidden`}>
      <div className="w-full max-w-[85rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-12 py-3 pb-8 space-y-2">
        <header className="min-w-0">
          <h1 className={`text-xl sm:text-2xl font-semibold leading-tight ${textPri}`}>
            {tab === 'cobranzas' ? 'Análisis de cobranza' : 'Reportes'}
          </h1>
          <p className={`text-xs ${textSec}`}>
            {tab === 'cobranzas'
              ? `${PERIODOS.find((p) => p.id === periodo)?.label || 'Período'} · ${rango.desde} – ${rango.hasta} · Para cobrar impagas, usá Cobrar.`
              : 'Flujo de caja, análisis de cobranza, estado de resultados y presupuestos.'}
          </p>
        </header>

        <div className={`rounded-xl border p-0.5 ${box}`}>
          <div className="flex gap-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={`flex-1 min-w-0 px-2 sm:px-3 py-1.5 rounded-lg text-[11px] sm:text-sm font-medium ${
                  tab === t.id ? 'bg-[#1F80FF] text-white' : tabInactive
                }`}
              >
                <span className="sm:hidden">{t.short}</span>
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>
        </div>

        <FormAlert type="error" className="mb-2">{error}</FormAlert>

        {tab !== 'presupuestos' && (
          <div className={`rounded-2xl border ${box}`}>
            <div className="px-3 py-2 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>Período</p>
                {(tab === 'flujo' || tab === 'pyl') && showCurrencyToggle && (
                  <CurrencyViewToggle
                    value={currencyVista}
                    onChange={setCurrencyVista}
                    monedas={tab === 'pyl' ? (pyl?.monedas || []) : (datos?.monedas || [])}
                    modoOscuro={modoOscuro}
                  />
                )}
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap gap-1.5">
                  {PERIODOS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPeriodo(p.id)}
                      className={`px-2.5 py-1 rounded-md text-[11px] font-semibold ${
                        periodo === p.id ? 'bg-[#1F80FF] text-white' : pillIdle
                      }`}
                    >
                      <span className="sm:hidden">{p.short}</span>
                      <span className="hidden sm:inline">{p.label}</span>
                    </button>
                  ))}
                </div>
                {hasPylAccess && (
                  <button
                    type="button"
                    onClick={descargarPackContador}
                    disabled={exportingPack}
                    className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border disabled:opacity-50 ${
                      modoOscuro ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {exportingPack ? 'Armando pack…' : 'Pack del contador'}
                  </button>
                )}
              </div>
              {(tab === 'flujo' || tab === 'pyl') && showCurrencyToggle && (
                <p className={`text-[11px] ${textMuted}`}>{vistaHint(currencyVista)}</p>
              )}
            </div>
          </div>
        )}

        {tab === 'presupuestos' && !hasPresupuestosAccess && (
          <PlanUpgradeCard
            title="Presupuestos por categoría"
            description="Topes mensuales por categoría y alertas de desvío cuando te pasás del tope."
            bullets={[
              'Presupuesto por categoría de egresos',
              'Alertas de desvío en Reportes',
              'Incluido en plan Empresarial',
            ]}
            planLabel="Empresarial"
            modoOscuro={modoOscuro}
            box={box}
            textPri={textPri}
            textMuted={textMuted}
          />
        )}

        {tab === 'presupuestos' && hasPresupuestosAccess && (
          <BudgetPanel
            modoOscuro={modoOscuro}
            box={box}
            textPri={textPri}
            textSec={textSec}
            textMuted={textMuted}
            btnSec={btnSec}
            divide={divide}
            divideY={divideY}
            formatMoney={formatMoney}
          />
        )}

        {tab === 'pyl' && !hasPylAccess && (
          <PlanUpgradeCard
            title="Estado de resultados"
            description="Ingresos, costos, utilidad bruta y resultado operativo con comparación vs período anterior."
            bullets={[
              'Resultado devengado con desglose por categoría',
              'Panel fiscal Monotributo / RI',
              'Exportación CSV del estado de resultados',
            ]}
            planLabel="Profesional"
            modoOscuro={modoOscuro}
            box={box}
            textPri={textPri}
            textMuted={textMuted}
          />
        )}

        {tab === 'cobranzas' && (
          <CollectionReportPanel
            rango={rango}
            modoOscuro={modoOscuro}
            box={box}
            textPri={textPri}
            textMuted={textMuted}
            divide={divide}
            hasProfesional={hasProfesional}
            hasCobranzasEssential={hasCobranzasEssential}
            secureFetch={secureFetch}
            formatMoney={formatMoney}
          />
        )}

        {tab === 'pyl' && hasPylAccess && (
          <>
            {loadingPyl ? (
              <div className={`rounded-xl sm:rounded-2xl border ${box} p-6 sm:p-8 text-center`}>
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1F80FF] mx-auto mb-3" />
                <p className={textMuted}>Cargando estado de resultados...</p>
              </div>
            ) : pyl ? (
              <PylStatement
                pyl={pyl}
                vista={currencyVista}
                onVistaChange={setCurrencyVista}
                rango={rango}
                modoOscuro={modoOscuro}
                box={box}
                textPri={textPri}
                textSec={textSec}
                textMuted={textMuted}
                divide={divide}
                formatMoney={formatMoney}
                onExport={exportarPylCsv}
                exporting={exportingPyl}
              />
            ) : (
              <div className={`rounded-2xl border border-dashed ${divide} p-8 sm:p-12 text-center`}>
                <p className={`text-sm ${textSec}`}>No hay datos para el período seleccionado.</p>
              </div>
            )}
          </>
        )}

        {tab === 'flujo' && hasPresupuestosAccess && (
          <ConsolidatedReportPanel
            modoOscuro={modoOscuro}
            desde={rango.desde}
            hasta={rango.hasta}
          />
        )}

        {tab === 'flujo' && (loading ? (
          <div className={`rounded-2xl border ${box} p-6 text-center`}>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F80FF] mx-auto mb-2" />
            <p className={`text-xs ${textMuted}`}>Cargando reporte…</p>
          </div>
        ) : datos ? (
          <>
            <section className={`rounded-2xl border overflow-hidden ${box}`}>
              <div className={`grid grid-cols-2 lg:grid-cols-5 gap-px ${modoOscuro ? 'bg-gray-800' : 'bg-gray-100'}`}>
                {[
                  { label: 'Ventas', value: formatFlujo(flujoVista.ventasTotal), hint: `${datos.cantidadVentas} registros`, color: textPri },
                  { label: 'Cobrado', value: formatFlujo(flujoVista.ventasCobradas), color: modoOscuro ? 'text-green-300' : 'text-green-700' },
                  { label: 'Pendiente', value: formatFlujo(flujoVista.ventasImpagas), color: modoOscuro ? 'text-amber-300' : 'text-amber-700' },
                  { label: 'Egresos', value: formatFlujo(flujoVista.egresosTotal), hint: `${datos.cantidadEgresos} gastos`, color: modoOscuro ? 'text-red-300' : 'text-red-700' },
                  {
                    label: 'Flujo',
                    value: formatFlujo(flujoVista.flujoCaja),
                    hint: 'Cobrado − Egresos',
                    color: flujoVista.flujoCaja >= 0
                      ? (modoOscuro ? 'text-green-300' : 'text-green-700')
                      : (modoOscuro ? 'text-red-300' : 'text-red-700'),
                    span: true,
                  },
                ].map((card) => (
                  <div
                    key={card.label}
                    className={`px-3 py-2.5 min-w-0 ${card.span ? 'col-span-2 lg:col-span-1' : ''} ${modoOscuro ? 'bg-gray-900' : 'bg-white'}`}
                  >
                    <div className={`text-[10px] font-semibold tracking-wide uppercase ${textMuted}`}>{card.label}</div>
                    <div className={`text-sm font-semibold tabular-nums break-words ${card.color}`}>{card.value}</div>
                    {card.hint && <div className={`text-[11px] ${textMuted}`}>{card.hint}</div>}
                  </div>
                ))}
              </div>
              {datos.sinCotizacion > 0 && (
                <p className={`text-[11px] px-3 py-2 border-t ${divide} ${modoOscuro ? 'text-amber-200' : 'text-amber-800'}`}>
                  {datos.sinCotizacion} registro(s) en moneda extranjera sin cotización.
                </p>
              )}
            </section>

            {hasProfesional && (
              <section className={`rounded-2xl border overflow-hidden ${box}`}>
                <div className={`px-3 py-2 border-b flex items-center gap-2 ${divide}`}>
                  <FiTrendingUp className="w-4 h-4 text-[#1F80FF]" />
                  <h2 className={`text-sm font-semibold ${textPri}`}>Flujo proyectado (3 meses)</h2>
                </div>
                {loadingProyeccion ? (
                  <p className={`text-xs px-3 py-4 ${textMuted}`}>Calculando proyección…</p>
                ) : (
                  <ProjectedCashflowPanel
                    proyeccion={proyeccion}
                    formatAmount={formatFlujo}
                    modoOscuro={modoOscuro}
                    divide={divide}
                    textSec={textSec}
                    textMuted={textMuted}
                  />
                )}
              </section>
            )}

            {categorias && categorias.length > 0 && (
              <section className={`rounded-2xl border overflow-hidden ${box}`}>
                <h2 className={`text-sm font-semibold px-3 py-2 border-b ${divide} ${textPri}`}>Por categoría</h2>
                <div className="grid grid-cols-1 gap-2 p-3 md:hidden">
                  {categorias.map((row) => (
                    <div key={row.categoria} className={`rounded-xl border p-3 ${modoOscuro ? 'border-gray-800 bg-gray-800/40' : 'border-gray-100 bg-gray-50'}`}>
                      <p className={`text-sm font-semibold ${textPri}`}>{row.categoria}</p>
                      <div className="mt-2 grid grid-cols-3 gap-2">
                        <div>
                          <p className={`text-[10px] uppercase font-semibold ${textMuted}`}>Cobrado</p>
                          <p className={`text-xs font-semibold tabular-nums break-words ${textPri}`}>{formatMoney(row.ventasCobradas)}</p>
                        </div>
                        <div>
                          <p className={`text-[10px] uppercase font-semibold ${textMuted}`}>Egresos</p>
                          <p className={`text-xs font-semibold tabular-nums break-words ${textPri}`}>{formatMoney(row.egresosTotal)}</p>
                        </div>
                        <div>
                          <p className={`text-[10px] uppercase font-semibold ${textMuted}`}>Margen</p>
                          <p className={`text-xs font-semibold tabular-nums break-words ${
                            row.margen >= 0
                              ? modoOscuro ? 'text-green-300' : 'text-green-700'
                              : modoOscuro ? 'text-red-300' : 'text-red-700'
                          }`}>
                            {formatMoney(row.margen)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`text-[11px] uppercase tracking-wide ${textMuted} ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
                        <th className="text-left font-semibold py-2.5 px-4">Categoría</th>
                        <th className="text-right font-semibold py-2.5 px-3">Cobrado</th>
                        <th className="text-right font-semibold py-2.5 px-3">Egresos</th>
                        <th className="text-right font-semibold py-2.5 px-4">Margen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {categorias.map((row) => (
                        <tr key={row.categoria} className={`border-t ${modoOscuro ? 'border-gray-800 hover:bg-gray-800/70' : 'border-gray-100 hover:bg-gray-50'}`}>
                          <td className={`py-2.5 px-4 text-sm font-medium ${textPri}`}>{row.categoria}</td>
                          <td className={`py-2.5 px-3 text-sm text-right tabular-nums ${textSec}`}>{formatMoney(row.ventasCobradas)}</td>
                          <td className={`py-2.5 px-3 text-sm text-right tabular-nums ${textSec}`}>{formatMoney(row.egresosTotal)}</td>
                          <td className={`py-2.5 px-4 text-sm text-right font-semibold tabular-nums ${
                            row.margen >= 0
                              ? modoOscuro ? 'text-green-300' : 'text-green-700'
                              : modoOscuro ? 'text-red-300' : 'text-red-700'
                          }`}>
                            {formatMoney(row.margen)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {mensual && mensual.meses && (
              <section className={`rounded-2xl border overflow-hidden ${box}`}>
                <div className={`flex items-center justify-between gap-2 px-3 py-2 border-b ${divide}`}>
                  <h2 className={`text-sm font-semibold ${textPri}`}>Por mes</h2>
                  <select
                    value={añoMensual}
                    onChange={(e) => setAñoMensual(Number(e.target.value))}
                    className={`px-2 py-1 rounded-md border text-xs ${modoOscuro ? 'bg-gray-800 text-white border-gray-700' : 'bg-white text-gray-900 border-gray-200'}`}
                  >
                    {getAñosDisponibles().map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 gap-2 p-3 md:hidden">
                  {mensual.meses.map((row) => {
                    const key = `${row.anno}-${row.mes}`;
                    const isExpanded = mesExpandido === key;
                    const dias = datosDiarios[key] || [];
                    return (
                      <div key={key} className={`rounded-xl border ${modoOscuro ? 'border-gray-800 bg-gray-800/40' : 'border-gray-100 bg-gray-50'}`}>
                        <button
                          type="button"
                          onClick={() => toggleMes(row.anno, row.mes)}
                          className="w-full text-left p-3"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className={`text-sm font-semibold capitalize ${textPri}`}>{row.nombreMes}</span>
                            <span className={`text-sm font-semibold tabular-nums ${
                              row.flujoCaja >= 0
                                ? modoOscuro ? 'text-green-300' : 'text-green-700'
                                : modoOscuro ? 'text-red-300' : 'text-red-700'
                            }`}>
                              {formatMoney(row.flujoCaja)}
                            </span>
                          </div>
                          <div className={`mt-1.5 grid grid-cols-2 gap-2 text-[11px] ${textMuted}`}>
                            <span>Cobrado {formatMoney(row.ventasCobradas)}</span>
                            <span className="text-right">Egresos {formatMoney(row.egresosTotal)}</span>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className={`px-3 pb-3 border-t ${divide}`}>
                            <p className={`pt-2 mb-1 text-[10px] uppercase font-semibold ${textMuted}`}>Desglose diario</p>
                            {cargandoDiario && !dias.length ? (
                              <p className={`text-xs py-2 ${textMuted}`}>Cargando días…</p>
                            ) : (
                              <DiarioList dias={dias} formatMoney={formatMoney} modoOscuro={modoOscuro} textMuted={textMuted} textPri={textPri} />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`text-[11px] uppercase tracking-wide ${textMuted} ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
                        <th className="text-left font-semibold py-2.5 px-4">Mes</th>
                        <th className="text-right font-semibold py-2.5 px-3">Cobrado</th>
                        <th className="text-right font-semibold py-2.5 px-3">Egresos</th>
                        <th className="text-right font-semibold py-2.5 px-4">Flujo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mensual.meses.map((row) => {
                        const key = `${row.anno}-${row.mes}`;
                        const isExpanded = mesExpandido === key;
                        const dias = datosDiarios[key] || [];
                        return (
                          <React.Fragment key={key}>
                            <tr
                              className={`cursor-pointer border-t ${modoOscuro ? 'border-gray-800 hover:bg-gray-800/70' : 'border-gray-100 hover:bg-gray-50'}`}
                              onClick={() => toggleMes(row.anno, row.mes)}
                            >
                              <td className={`py-2.5 px-4 text-sm font-medium capitalize ${textPri}`}>
                                <span className="inline-flex items-center gap-1.5">
                                  <svg className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  {row.nombreMes}
                                </span>
                              </td>
                              <td className={`py-2.5 px-3 text-sm text-right tabular-nums ${textSec}`}>
                                {formatMoney(row.ventasCobradas)}
                                {row.ventasTotal > row.ventasCobradas && (
                                  <span className={`block text-[11px] ${textMuted}`}>de {formatMoney(row.ventasTotal)}</span>
                                )}
                              </td>
                              <td className={`py-2.5 px-3 text-sm text-right tabular-nums ${textSec}`}>{formatMoney(row.egresosTotal)}</td>
                              <td className={`py-2.5 px-4 text-sm text-right font-semibold tabular-nums ${
                                row.flujoCaja >= 0
                                  ? modoOscuro ? 'text-green-300' : 'text-green-700'
                                  : modoOscuro ? 'text-red-300' : 'text-red-700'
                              }`}>
                                {formatMoney(row.flujoCaja)}
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={4} className={`px-4 py-3 ${modoOscuro ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                                  <p className={`mb-1 text-[10px] uppercase font-semibold ${textMuted}`}>Desglose diario</p>
                                  {cargandoDiario && !dias.length ? (
                                    <p className={`text-xs py-2 ${textMuted}`}>Cargando días…</p>
                                  ) : (
                                    <DiarioList dias={dias} formatMoney={formatMoney} modoOscuro={modoOscuro} textMuted={textMuted} textPri={textPri} />
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        ) : (
          <div className={`rounded-2xl border border-dashed ${divide} px-4 py-8 text-center`}>
            <p className={`text-sm ${textSec}`}>No hay datos para el período seleccionado.</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Reportes;
